<script setup>
import { computed } from "vue";
import ListRow from "./ListRow.vue";

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    lists: {
        type: Array,
        default: () => [],
    },
    viewAllUrl: {
        type: String,
        default: null,
    },
    showRowActions: {
        type: Boolean,
        default: true,
    },
    // Rows are bookmarks of other members' lists (see ListRow `saved`).
    saved: {
        type: Boolean,
        default: false,
    },
    // A bookmark toggle in flight elsewhere on the page (GH-1159):
    // { uuid, name, image, url, adding }. While adding, a ghost row for it
    // leads the list; while removing, the matching row throbs.
    pending: {
        type: Object,
        default: null,
    },
    // Path under public/ for a glyph beside the empty-state copy (Figma
    // 443-218 pairs Saved Lists with a red bookmark-plus).
    emptyIcon: {
        type: String,
        default: null,
    },
});

const ghost = computed(() => {
    if (!props.pending?.adding) {
        return null;
    }

    if (props.lists.some((list) => list.uuid === props.pending.uuid)) {
        return null;
    }

    return { ...props.pending, privacy: "public" };
});

const hasRows = computed(() => props.lists.length > 0 || ghost.value !== null);
</script>

<template>
    <section>
        <header class="mb-4 flex items-baseline justify-between gap-3">
            <h2 class="font-brand text-[20px] font-bold text-[#102a3d]">
                {{ title }}
            </h2>
            <a
                v-if="viewAllUrl && lists.length"
                :href="viewAllUrl"
                class="font-brand text-[15px] font-medium text-[#102a3d] underline underline-offset-2 transition-colors hover:text-brand-red"
            >
                View All
            </a>
        </header>

        <div v-if="hasRows" class="flex flex-col gap-3">
            <ListRow v-if="ghost" :list="ghost" :saved="saved" processing />
            <ListRow
                v-for="list in lists"
                :key="list.uuid"
                :list="list"
                :show-actions="showRowActions"
                :saved="saved"
                :processing="pending?.uuid === list.uuid"
            />
        </div>

        <!-- Second View All under the rows (Figma 443-218), so the link is in
             reach after scrolling the list. -->
        <footer v-if="viewAllUrl && lists.length" class="mt-5 flex justify-end">
            <a
                :href="viewAllUrl"
                class="font-brand text-[15px] font-medium text-[#102a3d] underline underline-offset-2 transition-colors hover:text-brand-red"
            >
                View All
            </a>
        </footer>

        <!-- Explicit condition: the View All footer sits between this and
             the rows' v-if, so a v-else would key off the footer instead. -->
        <div v-if="!hasRows" class="flex items-start gap-[17px]">
            <img
                v-if="emptyIcon"
                :src="asset(emptyIcon)"
                alt=""
                aria-hidden="true"
                class="mt-px h-6 w-[23px] shrink-0"
            />
            <!-- Typography on the <p> itself: legacy app.scss sets p line-height. -->
            <p
                class="m-0 max-w-[247px] font-brand text-[14px] font-medium leading-[1.4] text-[#102a3d]"
            >
                <slot name="empty">You don't have any lists yet.</slot>
            </p>
        </div>
    </section>
</template>
